import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  ListItem,
  MenuItem,
  Popover,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import { t } from 'i18next'
import DateRangePickerWithPresets from '../DateRangePickerWithPresets/DateRangePickerWithPresets'
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material'
import { useGetProjectsQuery } from '../../store/redux/reducers/projectsSlice'
import defaultProps from '../../config/defaultProps'
import { useSelector } from 'react-redux'
import { RootState } from '../../store/redux/store'
import { getCurrentUser, getIsManagement, getIsProjectLeader } from '../../store/redux/reducers/currentUserSlice'
import { Project } from '../../store/types'

export type FilterUpcomingVacationsPopupProps = {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  anchorEl: HTMLElement | null
  startDate: Dayjs | null
  endDate: Dayjs | null
  setStartDate: (date: Dayjs | null) => void
  setEndDate: (date: Dayjs | null) => void
  onApplyFilters?: (filters: { projects?: string[]; startDate: string; endDate: string; acknowledged: string }) => void
  onClearFilters?: () => void
}

const FilterUpcomingVacationsPopup: React.FC<FilterUpcomingVacationsPopupProps> = ({
  open,
  setOpen,
  anchorEl,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  onApplyFilters,
  onClearFilters,
}) => {
  const theme = useTheme()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  
  const [localStartDate, setLocalStartDateLocal] = useState<Dayjs | null>(startDate)
  const [localEndDate, setLocalEndDateLocal] = useState<Dayjs | null>(endDate)
  const [localProjectName, setLocalProjectName] = useState<Project[]>([])
  const [localAcknowledged, setLocalAcknowledged] = useState<string>('all')

  const [searchedAcronym, setSearchedAcronym] = useState<string>('')

  const [projectsPage, setProjectsPage] = useState<number>(defaultProps.page)
  const [projectsList, setProjectsList] = useState<Project[]>([])

  useEffect(() => {
    setLocalStartDateLocal(startDate)
    setLocalEndDateLocal(endDate)
  }, [startDate, endDate])

  useEffect(() => {
    if (open) {
      setProjectsPage(defaultProps.page)
      setSearchedAcronym('')
    }
  }, [open])

  const currentUser = useSelector(getCurrentUser)
  const isProjectLeader = useSelector((state: RootState) => getIsProjectLeader(state))
  const isManagement = useSelector((state: RootState) => getIsManagement(state))

  const { data: projectsResult, isSuccess } = useGetProjectsQuery(
    isProjectLeader && !isManagement
      ? {
          ...defaultProps,
          projectStatus: 'ACTIVE',
          'leaders.leader.id': currentUser?.id,
          page: projectsPage,
          acronym: searchedAcronym,
        }
      : { ...defaultProps, page: projectsPage, acronym: searchedAcronym },
    { skip: !open }
  )

  useEffect(() => {
    if (isSuccess && projectsResult) {
      setProjectsList(prev => {
        const combinedProjects =
          projectsPage === defaultProps.page ? projectsResult.content : [...prev, ...projectsResult.content]

        return combinedProjects.filter((project, index, self) => self.findIndex(p => p.id === project.id) === index)
      })
    }
  }, [projectsResult, isSuccess, projectsPage])

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const handleInputChange = useCallback((event: React.SyntheticEvent, value: string, _reason: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      setProjectsPage(defaultProps.page)

      if (value !== '') {
        setProjectsList([])
      }
      setSearchedAcronym(value)
    }, 500)
  }, [])

  const handleListboxScroll = useCallback(
    (e: React.UIEvent<HTMLUListElement, UIEvent>) => {
      const listboxNode = e.currentTarget
      if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 1) {
        if (projectsResult && projectsResult.totalPages && projectsPage < projectsResult.totalPages - 1) {
          setProjectsPage(prevPage => prevPage + 1)
        }
      }
    },
    [projectsResult, projectsPage]
  )

  const handleClose = (_event?: {}, reason?: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick') {
      handleClear()
    } else {
      setOpen(false)
    }
  }

  const handleApply = () => {
    setStartDate(localStartDate)
    setEndDate(localEndDate)

    if (onApplyFilters) {
      onApplyFilters({
        projects: localProjectName.map(p => p.id),
        startDate: localStartDate ? localStartDate.format('YYYY-MM-DD') : '',
        endDate: localEndDate ? localEndDate.format('YYYY-MM-DD') : '',
        acknowledged: localAcknowledged,
      })
    }
    setOpen(false)
  }

  const handleClear = () => {
    const next7DaysStart = dayjs()
    const next7DaysEnd = dayjs().add(7, 'day').endOf('day')

    setLocalStartDateLocal(next7DaysStart)
    setLocalEndDateLocal(next7DaysEnd)
    setLocalProjectName([])
    setLocalAcknowledged('all')
    setProjectsPage(defaultProps.page)
    setSearchedAcronym('')

    if (onClearFilters) onClearFilters()
    setOpen(false)
  }

  return (  
    <Popover
      open={open}
      onClose={handleClose}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{ width: '100%', maxWidth: 650 }}
    >
      <Box sx={{ p: 3, minWidth: isDownSm ? '100%' : '500px', gap: 4, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h5" component="span" alignItems="center" color="text.highEmphasis">
          {t('page.users.filtering.title')}
        </Typography>
        <DateRangePickerWithPresets
          startDate={localStartDate}
          endDate={localEndDate}
          setStartDate={setLocalStartDateLocal}
          setEndDate={setLocalEndDateLocal}
          presetsType="past"
        />
        <Autocomplete
          limitTags={3}
          sx={{ maxWidth: 'md' }}
          multiple
          id="upcoming-vacations-projects-autocomplete-filter"
          size="small"
          disableCloseOnSelect
          options={projectsList}
          value={localProjectName}
          onChange={(_, value) => {
            setLocalProjectName(value)
          }}
          noOptionsText={t('noOptionsFound')}
          onInputChange={handleInputChange}
          ListboxProps={{
            style: { maxHeight: '400px' },
            onScroll: handleListboxScroll,
          }}
          getOptionLabel={option => option.acronym}
          renderOption={(props, option, { selected }) => (
            <React.Fragment key={option.id}>
              <ListItem {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlank fontSize="small" />}
                  checkedIcon={<CheckBox fontSize="small" />}
                  checked={selected}
                  style={{ marginRight: 8 }}
                />
                {option.acronym}
              </ListItem>
            </React.Fragment>
          )}
          renderInput={params => (
            <TextField
              {...params}
              label={t('page.users.filtering.projects')}
              placeholder={t('page.users.filtering.projects')}
            />
          )}
        />
        <FormControl fullWidth>
          <InputLabel id="acknowledged-select-label">{t('components.upcomingVacation.filter.acknowledged')}</InputLabel>
          <Select
            labelId="acknowledged-select-label"
            id="acknowledged-select"
            value={localAcknowledged}
            label={t('components.upcomingVacation.filter.acknowledged')}
            onChange={e => setLocalAcknowledged(e.target.value)}
          >
            <MenuItem value="all">{t('components.upcomingVacation.filter.all')}</MenuItem>
            <MenuItem value="accepted">{t('components.upcomingVacation.filter.accepted')}</MenuItem>
            <MenuItem value="notaccepted">{t('components.upcomingVacation.filter.notaccepted')}</MenuItem>
          </Select>
        </FormControl>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="text" onClick={handleClear}>
            {t('components.upcomingVacation.filter.clear')}
          </Button>
          <Button variant="contained" onClick={handleApply}>
            {t('components.upcomingVacation.filter.apply')}
          </Button>
        </Stack>
      </Box>
    </Popover>
  )
}

export default FilterUpcomingVacationsPopup
